import React, { useState } from 'react';
import { useAlert } from '../contexts/AlertContext';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (currentPassword: string, newPassword: string) => Promise<void>;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { showToast } = useAlert();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 유효성 검사
  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    // 현재 비밀번호 확인
    if (!formData.currentPassword) {
      newErrors.currentPassword = '현재 비밀번호를 입력해주세요.';
    }

    // 새 비밀번호 확인
    if (!formData.newPassword) {
      newErrors.newPassword = '새 비밀번호를 입력해주세요.';
    } else if (formData.newPassword.length < 4) {
      newErrors.newPassword = '비밀번호는 최소 4자 이상이어야 합니다.';
    }

    // 새 비밀번호와 현재 비밀번호가 같은지 확인
    if (formData.currentPassword && formData.newPassword && formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = '새 비밀번호는 현재 비밀번호와 달라야 합니다.';
    }

    // 비밀번호 확인
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호 확인을 입력해주세요.';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = '비밀번호가 일치하지 않습니다.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData.currentPassword, formData.newPassword);

      // 성공 시 폼 초기화 및 모달 닫기
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setErrors({});
      showToast('비밀번호가 성공적으로 변경되었습니다.', 'success');
      onClose();
    } catch (error: any) {
      // 에러 처리
      if (error.message?.includes('현재 비밀번호')) {
        setErrors({ currentPassword: '현재 비밀번호가 올바르지 않습니다.' });
      } else {
        showToast(error.message || '비밀번호 변경에 실패했습니다.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-40"
        onClick={handleClose}
      ></div>
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl relative z-50">
        <header className="p-4 border-b border-slate-200 flex justify-between items-center">
          <h3 className="font-bold text-lg">비밀번호 변경</h3>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <div className="p-6 space-y-4">
          {/* 현재 비밀번호 */}
          <div>
            <label className="block font-medium text-slate-700 mb-1">
              현재 비밀번호 <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={formData.currentPassword}
              onChange={(e) => {
                setFormData({...formData, currentPassword: e.target.value});
                setErrors({...errors, currentPassword: ''});
              }}
              className={`w-full p-2 border rounded-md ${
                errors.currentPassword
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-slate-300 focus:border-indigo-500'
              }`}
              placeholder="현재 비밀번호를 입력하세요"
            />
            {errors.currentPassword && (
              <p className="text-xs text-red-500 mt-1">{errors.currentPassword}</p>
            )}
          </div>

          {/* 새 비밀번호 */}
          <div>
            <label className="block font-medium text-slate-700 mb-1">
              새 비밀번호 <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={formData.newPassword}
              onChange={(e) => {
                setFormData({...formData, newPassword: e.target.value});
                setErrors({...errors, newPassword: ''});
              }}
              className={`w-full p-2 border rounded-md ${
                errors.newPassword
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-slate-300 focus:border-indigo-500'
              }`}
              placeholder="새 비밀번호를 입력하세요 (최소 4자)"
            />
            {errors.newPassword && (
              <p className="text-xs text-red-500 mt-1">{errors.newPassword}</p>
            )}
          </div>

          {/* 비밀번호 확인 */}
          <div>
            <label className="block font-medium text-slate-700 mb-1">
              비밀번호 확인 <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => {
                setFormData({...formData, confirmPassword: e.target.value});
                setErrors({...errors, confirmPassword: ''});
              }}
              className={`w-full p-2 border rounded-md ${
                errors.confirmPassword
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-slate-300 focus:border-indigo-500'
              }`}
              placeholder="새 비밀번호를 다시 입력하세요"
            />
            {errors.confirmPassword && (
              <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>
            )}
          </div>

          {/* 안내 메시지 */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>안내:</strong>
              <br />• 비밀번호는 최소 4자 이상이어야 합니다.
              <br />• 현재 비밀번호와 다른 비밀번호를 설정해주세요.
            </p>
          </div>
        </div>

        <footer className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end space-x-2">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSubmitting ? '변경 중...' : '비밀번호 변경'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
